import { WS } from '@whistler-ui/k2/constants';
import { waitFor } from '@whistler-ui/k2/helpers';
import { useEffect, useRef, useState } from 'react';

import { useWebsheetStore } from '../store/websheet';

const websheetUrl = import.meta.env.REACT_APP_WEBSHEET_URL;

interface useWebsheetPopUpProps {
  resumeCallback: () => void;
  cancelCallback?: () => void;
}

export const useWebsheetPopUp = ({
  resumeCallback,
  cancelCallback,
}: useWebsheetPopUpProps) => {
  const { addInstance, removeInstance, popUpInstances } = useWebsheetStore();
  const [currentInstanceId, setCurrentInstanceId] = useState<string | null>(
    null,
  );
  /**
   * Executions whose websheet ended on an error screen.
   *
   * Their popups are left alone: the sheet is showing the carrier's message
   * (PSAP detail, Address Help, Edit Address) and that is what the user needs
   * to read. Navigating to the terminate page would replace it — and for a
   * negative certification test the execution status is Success, so the user
   * would be told "Test execution successful" over the top of the failure
   * they were verifying. The execution's real status is still on the test row
   * behind the popup.
   */
  const sheetErroredIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (popUpInstances && popUpInstances[currentInstanceId]) {
      const listener = (event: MessageEvent) => {
        if (event.data.type !== WS.WEBSHEETS_POPUP_MESSAGES) {
          return;
        }

        if (event.data.action === WS.RESUME_TEST_EXECUTION) {
          if (event.data.payload?.outcome === 'error' && currentInstanceId) {
            sheetErroredIds.current.add(currentInstanceId);
          }

          resumeCallback();
          return;
        }

        if (event.data.action === WS.CANCEL_TEST_EXECUTION) {
          cancelCallback();
        }
      };

      window.addEventListener('message', listener);

      return () => {
        window.removeEventListener('message', listener);
      };
    }
  }, [popUpInstances, currentInstanceId]);

  const startPopUpFlow = async execution => {
    const { id, websheet_url, status } = execution;
    const instance = popUpInstances[id];
    setCurrentInstanceId(id);

    if (instance) {
      instance.focus();

      if (
        id &&
        status !== WS.PAUSED &&
        status !== WS.PENDING &&
        !sheetErroredIds.current.has(id)
      ) {
        instance.postMessage(
          {
            type: WS.WEBSHEETS_POPUP_MESSAGES,
            action: WS.PROPAGATE_TEST_EXECUTION_COMPLETE,
            payload: { status },
          },
          websheetUrl,
        );

        const url = new URL(websheetUrl);
        url.pathname = '/terminate';
        url.searchParams.append('status', status);
        await waitFor(1500);
        instance.location.href = url.toString();
      }

      return;
    }

    if (status !== WS.PAUSED) {
      return;
    }

    const url = new URL(websheet_url);

    if (!url.searchParams.has('portal_test')) {
      url.searchParams.append('portal_test', 'true');
    }

    if (!url.searchParams.has('test_execution_id')) {
      url.searchParams.append('test_execution_id', id);
    }

    const newWindow = window.open(
      url.toString(),
      '_blank',
      'width=393,height=852,popup=true,left=20,top=60',
    );
    addInstance(id, newWindow);
  };

  return {
    startPopUpFlow,
  };
};
